// Начертание МАРКЕРОВ сноса (кружки). Вынесено из MapCanvas по той же причине,
// что и trackStyle.ts: девяносто строк MapLibre-выражений, подобранных на глаз,
// нечем было проверить, а от них зависит, читается ли картина вообще.
//
// Три роли одной точки отвечают на три разных вопроса, и словарь разведён:
//   origin — где ЗАРЕГИСТРИРОВАЛИ (отметка косяка / операция вылова);
//   today  — где объект СЕЙЧАС (опора при перемотке);
//   day    — где он на ВЫБРАННЫЙ день (это и есть ответ, потому полный цвет).
// Роль задаёт размер и заливку, ИСТОЧНИК — цвет. Смешивать нельзя: покрась мы
// отметку «цветом отметки», она отвязалась бы от своего трека.
import type { ExpressionSpecification, FilterSpecification } from 'maplibre-gl'

/** Цвет по источнику. Косяк — лазурный (тот же, что у TRACK_COLOR и маркера
 *  косяка: контраст к гамме течений «чёрный→зелёный» и к курс-синему судну),
 *  вылов — янтарный. */
export const DRIFT_SOURCE_COLOR = {
  cluster: '#0ea5e9',
  catch: '#e0a54b',
  other: '#888888',
} as const

/** Множитель размера по роли: ответ — в полный рост, опора и отметка — мельче. */
export const DRIFT_ROLE_SCALE = {
  day: 1,
  today: 0.55,
  origin: 0.55,
} as const

/** Базовая заливка по роли (до затухания по возрасту). Отметка — самая бледная:
 *  она контекст, а не ответ. Но не прозрачнее 0.15, иначе на тёмной карте её
 *  просто нет. */
export const DRIFT_ROLE_FILL = {
  day: 1,
  today: 0.4,
  origin: 0.3,
} as const

/** Толщина обводки по роли — у мелких ролей тоньше, иначе кружок съедается
 *  собственным контуром. */
export const DRIFT_ROLE_STROKE = {
  day: 1,
  today: 0.5,
  origin: 0.5,
} as const

/** Затухание по возрасту наблюдения: свежее — плотнее, но с полом (месячная
 *  отметка обязана оставаться находимой). */
const AGE_FADE: ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['coalesce', ['get', 'age_days'], 0],
  0,
  0.9,
  30,
  0.4,
]

/** Ветвление по роли: `match` от `role` с дневной веткой по умолчанию.
 *  Умолчание именно дневное — старые срезы (до появления ролей) роли не несут. */
function byRole(table: Record<'day' | 'today' | 'origin', number>): ExpressionSpecification {
  return [
    'match',
    ['get', 'role'],
    'today',
    table.today,
    'origin',
    table.origin,
    table.day,
  ] as ExpressionSpecification
}

/** Радиус по ступени размера, px. Одна шкала на оба источника — правка здесь
 *  меняет и маркеры карты, и кружки легенды (см. driftLegendScale).
 *
 *  Нижняя граница задана тестами: отметка происхождения рисуется в
 *  DRIFT_ROLE_SCALE.origin от радиуса, и дальнейшее уменьшение сделает малый
 *  улов неразличимой мушкой. Верхняя — читаемость самой карты: на промысле
 *  точки стоят кучно, и крупные кружки сливаются в пятно. */
export const DRIFT_GRADE_RADIUS = {
  small: 4,
  medium: 6,
  large: 9,
} as const

/** Ступени нет — нейтральный средний размер. Подставить «малый» значило бы
 *  выдать отсутствие эталона за слабый улов. */
export const DRIFT_GRADE_FALLBACK_RADIUS = DRIFT_GRADE_RADIUS.medium

/** Радиус: ступень размера × масштаб роли.
 *
 *  `grade` — единое поле бэкенда: у косяка это его `size_class`, у вылова —
 *  градация от эталона (доля от P95 самой подробной ступени с n≥20).
 *
 *  Раньше здесь ветвились по `source`, и ветка вылова считала радиус по
 *  АБСОЛЮТНЫМ тоннам с потолком 50 т. Отсюда две беды разом: 320-тонные сутки
 *  БМРТ выглядели как 51-тонные, а отличный для СЯМ 12-тонный день рисовался
 *  точкой. Теперь размер значит одно и то же независимо от источника. */
export function driftCircleRadius(): ExpressionSpecification {
  return [
    '*',
    byRole(DRIFT_ROLE_SCALE),
    [
      'match',
      ['get', 'grade'],
      'small',
      DRIFT_GRADE_RADIUS.small,
      'medium',
      DRIFT_GRADE_RADIUS.medium,
      'large',
      DRIFT_GRADE_RADIUS.large,
      DRIFT_GRADE_FALLBACK_RADIUS,
    ],
  ] as ExpressionSpecification
}

/** Что вообще попадает в слой точек сноса.
 *
 *  Точки — да, линии — нет (для них свои слои). Плюс отсев вылова со скрытыми
 *  точками: бэкенд отдаёт его геометрию сдвинутой на узел сетки, и рисовать по
 *  ней маркер нельзя — он выглядел бы точным местом операции. Область остаётся
 *  видна ореолом (см. driftHalo).
 *
 *  Признак один на одно состояние: «точки вылова скрыты настройкой кабинета» и
 *  «координаты огрублены» — это одно и то же (рефактор 13.08.2026). Ручные
 *  отметки косяков им не закрываются, и на них он не приходит.
 *
 *  Прячем ФИЛЬТРОМ, а не нулевой заливкой: невидимая точка всё равно ловит
 *  курсор, и карточка выдала бы координаты, которых видеть нельзя.
 *
 *  `!=` с литералом `true`, а не `== false`: у старых срезов свойства нет вовсе,
 *  и сравнение `== false` выкинуло бы с карты весь снос. */
export function driftPointFilter(): FilterSpecification {
  return [
    'all',
    ['==', ['geometry-type'], 'Point'],
    ['!=', ['get', 'anonymized'], true],
  ] as FilterSpecification
}

/** Цвет — только по источнику. */
export function driftCircleColor(): ExpressionSpecification {
  return [
    'match',
    ['get', 'source'],
    'cluster',
    DRIFT_SOURCE_COLOR.cluster,
    'catch',
    DRIFT_SOURCE_COLOR.catch,
    DRIFT_SOURCE_COLOR.other,
  ] as ExpressionSpecification
}

/** Заливка: полая у замёрзшей точки, иначе заливка роли с затуханием по возрасту.
 *
 *  Дневной маркер возрастом НЕ гасим — он ответ на вопрос «где объект сегодня»
 *  и не должен читаться бледнее статичной отметки; возраст несут линия и панель. */
export function driftCircleOpacity(): ExpressionSpecification {
  return [
    'case',
    ['coalesce', ['get', 'frozen'], false],
    0,
    ['==', ['get', 'role'], 'day'],
    DRIFT_ROLE_FILL.day,
    ['*', byRole(DRIFT_ROLE_FILL), AGE_FADE],
  ] as ExpressionSpecification
}

/** Обводка: толще у нестандартных статусов и у полых точек — без заливки маркер
 *  держится только на ней. */
export function driftCircleStrokeWidth(): ExpressionSpecification {
  return [
    '*',
    byRole(DRIFT_ROLE_STROKE),
    [
      'case',
      ['coalesce', ['get', 'frozen'], false],
      2.2,
      ['match', ['get', 'status'], 'beached', 1.8, 'gap', 1.8, 1],
    ],
  ] as ExpressionSpecification
}

/** Цвет обводки — статус: beached красный (берег), gap ИЛИ замёрзшая точка
 *  янтарный («дальше не знаем»), active белый. frozen приравнен к gap: для
 *  выбранного дня данных нет в обоих случаях. */
export function driftCircleStrokeColor(): ExpressionSpecification {
  return [
    'case',
    ['==', ['get', 'status'], 'beached'],
    '#ef4444',
    ['any', ['==', ['get', 'status'], 'gap'], ['coalesce', ['get', 'frozen'], false]],
    '#f59e0b',
    '#ffffff',
  ] as ExpressionSpecification
}
